// "Today's Top Pick" — header dropdown that fetches the daily curated product
// and gives one-tap affiliate access. Built to be the highest-converting button
// in the header (replaces the redundant "Browse picks" → /blog link).
//
// After NUDGE_DELAY_MS on-page (default 30s), the button morphs into a
// newsletter signup CTA ("Get weekly picks") to lift subscriber conversion.
// The nudge is dismissed permanently in localStorage after successful
// signup or explicit close; hidden on /admin routes so it doesn't
// distract the operator.

import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Sparkles, Star, ExternalLink, X, BookOpen, Mail, ArrowRight } from "lucide-react";
import api from "../../lib/api";
import ResponsiveImage from "./ResponsiveImage";

const NUDGE_DELAY_MS = 30_000;
const NUDGE_STORAGE_KEY = "kbl_newsletter_nudge_hide";

export default function TodaysPick() {
    const [open, setOpen] = useState(false);
    const [pick, setPick] = useState(null);
    const [loading, setLoading] = useState(false);
    // "pick" (default) or "newsletter" — swapped after NUDGE_DELAY_MS unless
    // the visitor has already dismissed or subscribed.
    const [mode, setMode] = useState("pick");
    const wrapRef = useRef(null);
    const { pathname } = useLocation();

    // Timer that flips the CTA into newsletter-signup mode after 30s on-page.
    // Skips admin routes and returning visitors flagged in localStorage.
    useEffect(() => {
        if (pathname.startsWith("/admin")) return;
        try {
            if (localStorage.getItem(NUDGE_STORAGE_KEY) === "1") return;
        } catch {
            // localStorage disabled — nudge every session, still fine.
        }
        const timer = setTimeout(() => setMode("newsletter"), NUDGE_DELAY_MS);
        return () => clearTimeout(timer);
    }, [pathname]);

    // Only lazily fetch the daily pick when the popover is opened *and* the
    // user is in pick mode — no wasted request when we're just showing the
    // newsletter nudge.
    useEffect(() => {
        if (open && mode === "pick" && !pick && !loading) {
            setLoading(true);
            api.get("/products/todays-pick")
                .then((r) => setPick(r.data))
                .catch(() => setPick({ _error: true }))
                .finally(() => setLoading(false));
        }
    }, [open, mode, pick, loading]);

    // Close on outside click and Escape.
    useEffect(() => {
        if (!open) return;
        const onClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => e.key === "Escape" && setOpen(false);
        document.addEventListener("mousedown", onClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const amazon =
        pick?.retailers?.find((r) => /amazon/i.test(r.name) || /amazon\./i.test(r.url)) || null;
    const otherRetailer =
        pick?.retailers?.find((r) => r !== amazon) || null;

    const dismissNudge = () => {
        try {
            localStorage.setItem(NUDGE_STORAGE_KEY, "1");
        } catch {
            // no-op — session-only nudge is fine.
        }
        setMode("pick");
        setOpen(false);
    };

    return (
        <div ref={wrapRef} className="relative hidden md:block">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="header-todays-pick"
                aria-expanded={open}
                aria-haspopup="true"
                className="inline-flex items-center gap-2 bg-kblcoral hover:bg-kblcoraldeep text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-colors"
            >
                {mode === "newsletter" ? (
                    <>
                        <Mail className="w-4 h-4" strokeWidth={2.4} />
                        Get weekly picks
                    </>
                ) : (
                    <>
                        <Sparkles className="w-4 h-4" strokeWidth={2.4} />
                        Today's pick
                    </>
                )}
            </button>

            {open && (
                <div
                    data-testid="todays-pick-popover"
                    className="absolute right-0 mt-2 w-[340px] bg-white border border-kblborder rounded-3xl shadow-2xl p-5 z-50"
                >
                    <div className="flex items-center justify-between mb-3">
                        {mode === "newsletter" ? (
                            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-kblcoraldeep flex items-center gap-1.5">
                                <Mail className="w-3.5 h-3.5" /> One pick a week — no spam
                            </p>
                        ) : (
                            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-kblcoraldeep flex items-center gap-1.5">
                                <Sparkles className="w-3.5 h-3.5" /> Kiblco's pick of the day
                            </p>
                        )}
                        <button
                            onClick={mode === "newsletter" ? dismissNudge : () => setOpen(false)}
                            data-testid="todays-pick-close"
                            className="p-1 rounded-full hover:bg-kblbg text-kblmuted"
                            aria-label="Close"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    {mode === "newsletter" && (
                        <NewsletterSignup
                            onSubscribed={() => {
                                try {
                                    localStorage.setItem(NUDGE_STORAGE_KEY, "1");
                                } catch {
                                    // no-op
                                }
                            }}
                            onShowPick={() => setMode("pick")}
                        />
                    )}

                    {mode === "pick" && loading && (
                        <p className="text-sm text-kblmuted py-8 text-center">Loading today's pick…</p>
                    )}

                    {mode === "pick" && pick?._error && (
                        <p className="text-sm text-kblcoraldeep py-6 text-center">
                            Couldn't load today's pick — try again in a moment.
                        </p>
                    )}

                    {mode === "pick" && pick && !pick._error && (
                        <PickCard pick={pick} amazon={amazon} other={otherRetailer} onNavigate={() => setOpen(false)} />
                    )}
                </div>
            )}
        </div>
    );
}

function PickCard({ pick, amazon, other, onNavigate }) {
    return (
        <>
            {pick.image && (
                <div className="h-44 rounded-2xl overflow-hidden bg-kblbg mb-3">
                    <ResponsiveImage
                        src={pick.image}
                        alt={pick.name}
                        width={640}
                        height={360}
                        sizes="(min-width: 1024px) 22vw, 90vw"
                        className="w-full h-full object-cover"
                    />
                </div>
            )}
            <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className="font-display font-bold text-base leading-snug text-kblink">
                    {pick.name}
                </h3>
                {pick.price_range && (
                    <span className="shrink-0 text-[10px] font-bold uppercase tracking-[0.12em] text-kblmuted bg-kblyellow/70 px-2 py-1 rounded-full">
                        {pick.price_range}
                    </span>
                )}
            </div>
            {pick.brand && (
                <p className="text-xs text-kblmuted mb-2 uppercase tracking-[0.12em] font-semibold">
                    {pick.brand}
                </p>
            )}
            {pick.rating ? (
                <div className="inline-flex items-center gap-1 text-xs font-semibold text-kblink mb-2">
                    <Star className="w-3.5 h-3.5 fill-kblyellow text-kblyellow" />
                    {pick.rating.toFixed(1)}
                </div>
            ) : null}
            {pick.short_review || pick.description ? (
                <p className="text-sm text-kblmuted leading-relaxed mb-4 line-clamp-3">
                    {pick.short_review || pick.description}
                </p>
            ) : null}

            <div className="flex flex-col gap-2">
                {amazon?.url && (
                    <a
                        href={amazon.url}
                        target="_blank"
                        rel="noopener noreferrer sponsored"
                        onClick={onNavigate}
                        data-testid="todays-pick-amazon"
                        className="inline-flex items-center justify-center gap-1.5 bg-kblcoral hover:bg-kblcoraldeep text-white text-sm font-semibold px-4 py-2.5 rounded-full transition-colors"
                    >
                        View on {amazon.name}
                        <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                )}
                {other?.url && (
                    <a
                        href={other.url}
                        target="_blank"
                        rel="noopener noreferrer sponsored"
                        onClick={onNavigate}
                        data-testid="todays-pick-secondary"
                        className="inline-flex items-center justify-center gap-1.5 bg-white border border-kblborder hover:border-kblink text-kblink text-sm font-semibold px-4 py-2.5 rounded-full transition-colors"
                    >
                        Shop at {other.name}
                        <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                )}
                {pick.linked_post?.slug && (
                    <Link
                        to={`/blog/${pick.linked_post.slug}`}
                        onClick={onNavigate}
                        data-testid="todays-pick-review"
                        className="inline-flex items-center justify-center gap-1.5 text-xs uppercase tracking-[0.14em] font-bold text-kblmuted hover:text-kblink pt-1"
                    >
                        <BookOpen className="w-3.5 h-3.5" /> Read our full review
                    </Link>
                )}
            </div>
        </>
    );
}


// Inline newsletter signup rendered inside the header popover once the
// 30s nudge fires. Success/error copy is minimal to fit the popover width;
// the API returns 400 for bad emails so we surface Beehiiv's rejection
// message when present.
function NewsletterSignup({ onSubscribed, onShowPick }) {
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState("idle"); // idle | loading | success | error
    const [errorMsg, setErrorMsg] = useState("");

    async function submit(e) {
        e.preventDefault();
        if (status === "loading") return;
        setStatus("loading");
        setErrorMsg("");
        try {
            await api.post("/newsletter", { email: email.trim(), source: "header-nudge" });
            setStatus("success");
            onSubscribed && onSubscribed();
        } catch (err) {
            setStatus("error");
            setErrorMsg(
                err?.response?.data?.detail ||
                    "Couldn't subscribe just now — please try again in a moment.",
            );
        }
    }

    if (status === "success") {
        return (
            <div className="py-4 text-center" data-testid="newsletter-nudge-success">
                <p className="text-2xl mb-1">🎉</p>
                <p className="font-display font-bold text-kblink mb-1">You're in!</p>
                <p className="text-sm text-kblmuted leading-relaxed">
                    One vet-informed pick lands in your inbox every Monday.
                </p>
            </div>
        );
    }

    return (
        <>
            <p className="text-sm text-kblmuted leading-relaxed mb-4">
                Get one hand-tested pet product pick every Monday. No spam, unsubscribe anytime.
            </p>
            <form onSubmit={submit} className="flex flex-col gap-2" data-testid="newsletter-nudge-form">
                <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="newsletter-nudge-email"
                    autoComplete="email"
                    className="w-full px-4 py-2.5 rounded-full border border-kblborder text-sm focus:outline-none focus:border-kblink transition-colors"
                />
                <button
                    type="submit"
                    disabled={status === "loading"}
                    data-testid="newsletter-nudge-submit"
                    className="inline-flex items-center justify-center gap-1.5 bg-kblcoral hover:bg-kblcoraldeep disabled:opacity-60 text-white text-sm font-semibold px-4 py-2.5 rounded-full transition-colors"
                >
                    {status === "loading" ? "Subscribing…" : (
                        <>
                            Subscribe
                            <ArrowRight className="w-3.5 h-3.5" />
                        </>
                    )}
                </button>
                {status === "error" && (
                    <p
                        data-testid="newsletter-nudge-error"
                        className="text-xs text-kblcoraldeep leading-snug px-1"
                    >
                        {errorMsg}
                    </p>
                )}
            </form>
            <button
                type="button"
                onClick={onShowPick}
                data-testid="newsletter-nudge-showpick"
                className="w-full mt-3 text-xs uppercase tracking-[0.14em] font-bold text-kblmuted hover:text-kblink transition-colors"
            >
                Show today's pick instead →
            </button>
        </>
    );
}
